import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";
import { getCookieConsent, setCookieConsent } from "@/lib/security";
import { cn } from "@/lib/utils";

/**
 * Banner de consentimento de cookies — LGPD Art. 7º, I.
 *
 * Aparece somente se o usuário ainda não escolheu aceitar/rejeitar.
 * Cookies essenciais (sessão Supabase, tema, este consentimento) são
 * sempre carregados — base legal: legítimo interesse / execução de contrato.
 */
export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  useEffect(() => {
    if (getCookieConsent() === null) setVisible(true);
  }, []);

  if (!visible) return null;

  function decide(status: "accepted" | "rejected") {
    setCookieConsent(status);
    setVisible(false);
  }

  return (
    <div
      role="dialog"
      aria-label="Aviso de cookies e privacidade"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 lg:left-auto lg:right-6 lg:max-w-md z-[100]"
    >
      <div
        className={cn(
          "rounded-2xl p-4 shadow-2xl border backdrop-blur-xl",
          isDark
            ? "bg-slate-900/85 border-white/15 text-slate-100"
            : "bg-white/90 border-slate-900/10 text-slate-800"
        )}
      >
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "size-9 rounded-xl grid place-items-center shrink-0",
              isDark ? "bg-[#33CCFF]/15 text-[#33CCFF]" : "bg-[#1A66CC]/10 text-[#1A66CC]"
            )}
          >
            <Cookie className="size-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold mb-1">Privacidade & Cookies</div>
            <p className={cn("text-xs leading-relaxed", isDark ? "text-slate-300" : "text-slate-600")}>
              Usamos cookies essenciais para autenticação e preferências. Os arquivos XML são
              processados <strong>100% no seu navegador</strong> — não enviamos seu conteúdo fiscal a servidores.
              Veja a{" "}
              <Link to="/privacidade" className="underline hover:no-underline">
                Política de Privacidade
              </Link>{" "}
              e os{" "}
              <Link to="/termos" className="underline hover:no-underline">
                Termos de Uso
              </Link>
              .
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Button size="sm" onClick={() => decide("accepted")} className="h-8 px-3 text-xs">
                <Shield className="size-3.5 mr-1.5" /> Aceitar todos
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => decide("rejected")}
                className="h-8 px-3 text-xs"
              >
                Apenas essenciais
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
